// DrawOnScroll — ported from Framer module qcrndbLNYAurUCnwJcoM/QqI7NH8QwpBe0JM85dS4
// Adds a `scrollLinked` mode: the drawing progress is bound to the element's
// scroll position (0% when it enters at the bottom of the viewport → 100% when
// it reaches the vertical center of the viewport).
import { useRef, useEffect, useState, startTransition } from 'react';
import { motion, useInView, useScroll } from 'framer-motion';

const EASINGS = {
    linear: 'linear',
    easeIn: 'easeIn',
    easeOut: 'easeOut',
    easeInOut: 'easeInOut',
    circOut: 'circOut',
    backOut: [.34, 1.56, .64, 1]
};

const useIsStaticRenderer = () => false;

function ScrollLinkedPath({ index, total, progress, transform, pathProps }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const len = typeof el.getTotalLength === 'function' ? el.getTotalLength() : 0;
        // Each path owns a slice of the scroll window, slightly overlapped so the
        // whole signature resolves progressively instead of path-by-path.
        const seg = 1 / Math.max(1, total);
        const start = index * seg * 0.78;
        const end = Math.min(1, start + seg * 1.9);
        const apply = (p) => {
            let t = (p - start) / Math.max(0.0001, end - start);
            t = t < 0 ? 0 : t > 1 ? 1 : t;
            if (len) {
                el.style.strokeDasharray = `${len} ${len}`;
                el.style.strokeDashoffset = `${len * (1 - t)}`;
            }
            el.style.opacity = t > 0.02 ? '1' : '0';
            el.style.fillOpacity = t > 0.6 ? String(Math.min(1, (t - 0.6) / 0.4)) : '0';
        };
        apply(progress.get());
        const unsubscribe = progress.on('change', apply);
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
            else if (progress.off) progress.off('change', apply);
        };
    }, [progress, index, total]);
    return (
        <g transform={transform}>
            <path ref={ref} {...pathProps} />
        </g>
    );
}

export default function DrawOnScroll(props) {
    const {
        paths = [{ d: 'M10 80 C 40 10, 65 10, 95 80 S 150 150, 180 80' }],
        viewBoxWidth = 200,
        viewBoxHeight = 160,
        sizing = 'fit',
        strokeColor = '#0099FF',
        strokeWidth = 6,
        strokeLinecap = 'round',
        strokeLinejoin = 'round',
        direction = 'forward',
        fillEnabled = false,
        fillColor = '#0099FF',
        duration = 1.5,
        delay = 0,
        stagger = .2,
        easing = 'easeInOut',
        triggerPosition = 20,
        triggerAmount = 0,
        replay = false,
        loop = false,
        loopStyle = 'mirror',
        loopDelay = 0,
        fadeOut = .4,
        highlightPath = -1,
        scrollLinked = false,
        onDrawStart,
        onDrawComplete,
        style
    } = props;

    const isStatic = useIsStaticRenderer();
    const ref = useRef(null);

    // Scroll-linked progress: 0 when the element's top enters the bottom of the
    // viewport, 1 when the element's top reaches the vertical center.
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'start center']
    });

    const triggered = useInView(ref, { amount: triggerAmount, margin: `0px 0px -${triggerPosition}% 0px` });
    const onScreen = useInView(ref);
    const [drawn, setDrawn] = useState(false);

    useEffect(() => {
        if (triggered) {
            startTransition(() => setDrawn(true));
        } else if (replay && !onScreen) {
            startTransition(() => setDrawn(false));
        }
    }, [triggered, onScreen, replay]);

    // Draw lifecycle callbacks (kept in refs so inline handlers don't retrigger)
    const drawStartRef = useRef(onDrawStart);
    const drawCompleteRef = useRef(onDrawComplete);
    useEffect(() => { drawStartRef.current = onDrawStart; }, [onDrawStart]);
    useEffect(() => { drawCompleteRef.current = onDrawComplete; }, [onDrawComplete]);

    const lastDrawnRef = useRef(false);
    useEffect(() => {
        if (drawn && !lastDrawnRef.current) {
            lastDrawnRef.current = true;
            const total = (delay + Math.max(0, paths.length - 1) * stagger + duration) * 1e3 + 150;
            const timer = window.setTimeout(() => drawCompleteRef.current?.(), total);
            return () => window.clearTimeout(timer);
        }
        if (!drawn && lastDrawnRef.current) {
            lastDrawnRef.current = false;
            drawStartRef.current?.();
        }
    }, [drawn, delay, stagger, duration, paths.length]);

    const ease = EASINGS[easing] ?? 'easeInOut';

    const compLen = paths.reduce((max, p, i) => Math.max(max, (p.delay ?? i * stagger) + (p.duration ?? duration)), 0);

    const restartLoop = loop && loopStyle === 'loop';
    const mirrorLoop = loop && loopStyle === 'mirror';

    const [loopTick, setLoopTick] = useState(0);
    const [fadingOut, setFadingOut] = useState(false);

    useEffect(() => {
        if (!restartLoop || !drawn || !onScreen || isStatic) {
            setFadingOut(false);
            return;
        }
        setFadingOut(false);
        const drawMs = (delay + compLen + loopDelay) * 1e3;
        const fadeMs = fadeOut * 1e3;
        if (drawMs <= 0) return;
        let fadeTimer = 0;
        let remountTimer = 0;
        const cycle = () => {
            fadeTimer = window.setTimeout(() => {
                startTransition(() => setFadingOut(true));
                remountTimer = window.setTimeout(() => {
                    startTransition(() => { setFadingOut(false); setLoopTick(t => t + 1); });
                    cycle();
                }, fadeMs);
            }, drawMs);
        };
        cycle();
        return () => { window.clearTimeout(fadeTimer); window.clearTimeout(remountTimer); };
    }, [restartLoop, drawn, onScreen, isStatic, delay, compLen, loopDelay, fadeOut]);

    const sharedPathProps = (path) => ({
        d: path.d,
        stroke: path.strokeColor ?? strokeColor,
        strokeWidth: path.strokeWidth ?? strokeWidth,
        strokeLinecap,
        strokeLinejoin,
        fill: fillEnabled ? fillColor : 'none'
    });

    const preserveAspectRatio = sizing === 'fill' ? 'xMidYMid slice' : sizing === 'stretch' ? 'none' : 'xMidYMid meet';

    const wrapperKey = restartLoop ? `restart-${loopTick}` : mirrorLoop ? `mirror-${onScreen}` : 'static';

    return (
        <div
            ref={ref}
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                aspectRatio: `${viewBoxWidth} / ${viewBoxHeight}`,
                ...style
            }}
        >
            <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                preserveAspectRatio={preserveAspectRatio}
                fill="none"
                style={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}
            >
                <motion.g
                    key={wrapperKey}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: fadingOut ? 0 : 1 }}
                    transition={{ duration: fadeOut, ease: 'easeInOut' }}
                >
                    {paths.map((path, index) => {
                        const { x = 0, y = 0, scale = 1 } = path;
                        const transform = `translate(${x} ${y}) scale(${scale})`;
                        const pathDir = !path.direction || path.direction === 'inherit' ? direction : path.direction;
                        const emptyOffset = pathDir === 'reverse' ? 1 : 0;

                        if (scrollLinked) {
                            return (
                                <ScrollLinkedPath
                                    key={index}
                                    index={index}
                                    total={paths.length}
                                    progress={scrollYProgress}
                                    transform={transform}
                                    pathProps={sharedPathProps(path)}
                                />
                            );
                        }

                        if (isStatic) {
                            const dimmed = highlightPath >= 0 && highlightPath !== index;
                            return (
                                <g key={index} transform={transform}>
                                    <path {...sharedPathProps(path)} opacity={dimmed ? .12 : 1} />
                                </g>
                            );
                        }

                        const startDelay = delay + (path.delay ?? index * stagger);
                        const dur = path.duration ?? duration;
                        const loopGap = compLen + loopDelay - dur;

                        return (
                            <g key={index} transform={transform}>
                                <motion.path
                                    {...sharedPathProps(path)}
                                    initial={{ pathLength: 0, pathOffset: emptyOffset, opacity: 0, fillOpacity: fillEnabled ? 0 : undefined }}
                                    animate={drawn
                                        ? { pathLength: 1, pathOffset: 0, opacity: 1, fillOpacity: fillEnabled ? 1 : undefined }
                                        : { pathLength: 0, pathOffset: emptyOffset, opacity: 0, fillOpacity: fillEnabled ? 0 : undefined }}
                                    transition={drawn
                                        ? {
                                            pathLength: { duration: dur, delay: startDelay, ease: ease, ...(mirrorLoop && onScreen ? { repeat: Infinity, repeatType: loopStyle, repeatDelay: loopGap } : {}) },
                                            pathOffset: { duration: dur, delay: startDelay, ease: ease, ...(mirrorLoop && onScreen ? { repeat: Infinity, repeatType: loopStyle, repeatDelay: loopGap } : {}) },
                                            opacity: { duration: .001, delay: startDelay },
                                            fillOpacity: {
                                                duration: dur * .4, delay: startDelay + dur * .6, ease: 'easeIn',
                                                ...(mirrorLoop && onScreen ? { repeat: Infinity, repeatType: loopStyle, repeatDelay: loopGap + dur * .6 } : {})
                                            }
                                        }
                                        : {
                                            pathLength: { duration: 0 },
                                            pathOffset: { duration: 0 },
                                            opacity: { duration: 0 },
                                            fillOpacity: { duration: 0 }
                                        }}
                                />
                            </g>
                        );
                    })}
                </motion.g>
            </svg>
        </div>
    );
}
